import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Select, Tooltip, ConfigProvider, Spin } from "antd";
import { Option } from "antd/es/mentions";
import { InputNumber } from "syngenta-digital-cropwise-react-ui-kit";
import infoIcon from "../../../assets/images/info.svg";
import { useNewTrialState } from "../../../pages/new-trail/hooks/NewTrialStateProvider";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../utils/translationUtils";
import "./style.less";
import AxiosInstance from "../../utils/axios";
import PlatFormAPIAxiosInstance from "../../utils/platFormAxios";
import { DropdownEmptyState } from "../DropdownEmptyState";
import { restrictToTwoDecimals } from "../../../pages/create-protocol/utils";
import { useTrialsRouter } from "../../custom-hooks/useTrialsRouter";

// Safety valve: the crop catalog is paginated and `last` is trusted to end the
// loop -- cap the walk so a malformed page (one that never reports `last`)
// can never spin forever. 100 pages * 200/page covers 20,000 crops, well
// beyond any single country's real catalog size.
const MAX_CROP_CATALOG_PAGES = 100;

// Same safety valve for the seed catalog walk below. 100 pages * 1000/page
// covers 100,000 seed varieties for a single crop, well beyond any real
// catalog size.
const MAX_SEED_CATALOG_PAGES = 100;

// <Select showSearch> has no `optionFilterProp`/`filterOption` of its own, so
// it falls back to matching against each Option's `value` -- a catalog UUID,
// never what the user types. Match against the product's displayed name
// instead (case-insensitive substring, same as the create-protocol Products
// screen's client-side search).
export const matchesSeedProductSearch = (searchText, product) =>
  (product?.commercial_name ?? "")
    .toLowerCase()
    .includes(String(searchText ?? "").toLowerCase());

const StandardSeedRate = () => {
  const { t } = useTranslation();
  const {
    ratesDosage,
    setRatesDosage,
    trialInfo,
    setIsStandardDataValid,
    trialProtocolData,
  } = useNewTrialState();
  const { orgId, workspaceId } = useTrialsRouter();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Paginated fetch (below) now takes up to MAX_SEED_CATALOG_PAGES sequential
  // round trips instead of one. If `trialInfo.protocol_id` changes while an
  // older fetch is still paging, only the LATEST fetch may apply its result --
  // otherwise an older walk resolving after a newer one has already finished
  // would overwrite the dropdown with the wrong protocol's products.
  const latestFetchReqRef = useRef(0);

  const extraHeaders = {
    "org-id": orgId,
    "workspace-id": workspaceId ?? "",
  };

  const querySeedCatalog = async (localizedCropIds, reqId) => {
    if (localizedCropIds.length === 0) return [];

    const seeds = [];
    let page = 1;
    let isLastPage = false;

    while (!isLastPage && page <= MAX_SEED_CATALOG_PAGES) {
      // Bail out mid-walk as soon as a newer protocol fetch has superseded
      // this one, instead of burning through the remaining pages for a
      // result nothing will use.
      if (reqId !== latestFetchReqRef.current) {
        return seeds;
      }

      const response = await PlatFormAPIAxiosInstance.post(
        `/catalog/seeds/query?page=${page}&size=1000`,
        { localized_crop_id: localizedCropIds, show_canonical: true }
      );

      seeds.push(...(response?.data?.content ?? []));
      isLastPage = response?.data?.last ?? true;
      page += 1;
    }

    return seeds;
  };

  // The seed catalog is keyed by COUNTRY-LOCALIZED crop ids
  // (`localized_crops[].id` on a /catalog/crops entry), which are a different
  // id space than a crop's canonical `id`. A protocol's `crop_details` can
  // carry the canonical id in BOTH `crop_id` and `localized_crop_ids` -- the
  // catalog then matches nothing and answers 200 with `content: []`, leaving
  // this dropdown silently empty. Re-resolve the localized id from the
  // country's crop catalog, the same way BasicInfoComponent does.
  const resolveLocalizedCropIds = async (cropId, country) => {
    if (!cropId || !country) return [];

    const catalogCrops = [];
    let page = 1;
    let isLastPage = false;

    while (!isLastPage && page <= MAX_CROP_CATALOG_PAGES) {
      const response = await PlatFormAPIAxiosInstance.get(
        `catalog/crops?include_deleted_crop=false&country=${country}&page=${page}&size=200`
      );

      catalogCrops.push(...(response?.data?.content ?? []));
      isLastPage = response?.data?.last ?? true;
      page += 1;
    }

    const matchingCrop = catalogCrops.find(
      (crop) =>
        crop?.id === cropId ||
        (crop?.localized_crops ?? []).some(
          (localizedCrop) => localizedCrop?.id === cropId
        )
    );

    const normalizedCountry = country.toUpperCase();

    const localizedCrop = (matchingCrop?.localized_crops ?? []).find(
      (entry) => (entry?.location ?? "").toUpperCase() === normalizedCountry
    );

    return localizedCrop?.id ? [localizedCrop.id] : [];
  };

  const getProtocolData = useCallback(async () => {
    // Claim a request id BEFORE awaiting. An earlier in-flight fetch becomes
    // stale the moment a newer one starts, and must not apply its result.
    const reqId = ++latestFetchReqRef.current;

    try {
      const protocolData = await AxiosInstance.get(
        `/protocol/${trialInfo?.protocol_id}`,
        { headers: extraHeaders }
      );

      const cropDetails = protocolData?.data?.crop_details;

      const storedCropIds = (
        Array.isArray(cropDetails?.localized_crop_ids) &&
        cropDetails.localized_crop_ids.length > 0
          ? cropDetails.localized_crop_ids
          : [cropDetails?.crop_id]
      ).filter(Boolean);

      let seeds = await querySeedCatalog(storedCropIds, reqId);

      // Empty is not necessarily "no varieties exist" -- far more often the
      // protocol's stored id is the canonical one. Retry once against the
      // freshly resolved localized id before giving up.
      if (seeds.length === 0) {
        const resolvedCropIds = (
          await resolveLocalizedCropIds(
            cropDetails?.crop_id,
            cropDetails?.country
          )
        ).filter((id) => !storedCropIds.includes(id));

        if (resolvedCropIds.length > 0) {
          seeds = await querySeedCatalog(resolvedCropIds, reqId);
        }
      }

      // Drop out-of-order responses; only the latest fetch may apply.
      if (reqId !== latestFetchReqRef.current) {
        return;
      }

      setProducts(seeds);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      if (reqId === latestFetchReqRef.current) {
        setLoading(false);
      }
    }
  }, [trialInfo?.protocol_id]);

  useEffect(() => {
    if (trialInfo?.protocol_id) {
      setLoading(true);

      getProtocolData();
    }
  }, [trialInfo?.protocol_id, getProtocolData]);

  const selectedProductValue = products?.some(
    (product) => product?.id === ratesDosage?.standard_seed_product_id
  )
    ? ratesDosage?.standard_seed_product_id
    : ratesDosage?.standard_product ?? undefined;

  // Built once per `products` change instead of scanning the full catalog
  // per Option inside filterOption -- antd calls filterOption once per
  // rendered Option on every keystroke, so an O(n) .find() there is O(n^2)
  // per keystroke across the whole catalog.
  // `products` starts as [] and is only ever replaced with the seed array
  // built above, so there is no nullish case to guard here.
  const productsById = useMemo(
    () => new Map(products.map((p) => [p?.id, p])),
    [products]
  );

  useEffect(() => {
    if (
      ratesDosage.standard_seed_rate == null ||
      ratesDosage.standard_seed_rate === 0 ||
      !ratesDosage.standard_product
    ) {
      setIsStandardDataValid(false);
    } else {
      setIsStandardDataValid(true);
    }
  }, [
    ratesDosage.standard_seed_rate,
    ratesDosage.standard_product,
    setIsStandardDataValid,
  ]);

  const handleStandardSeedRateChange = (seedRate) => {
    const updatedRatesAndDosage = {
      ...ratesDosage,
      standard_seed_rate: seedRate,
    };

    setRatesDosage(updatedRatesAndDosage);
  };

  const handleStandardProductChange = (product) => {
    const productName = products?.find(
      (p) => p.id === product
    )?.commercial_name;

    const updatedRatesAndDosage = {
      ...ratesDosage,
      standard_product: productName,
      standard_seed_product_id: product,
    };

    setRatesDosage(updatedRatesAndDosage);
  };

  const shouldDisplayRateError = () =>
    Boolean(
      ratesDosage.standard_product &&
        (!ratesDosage.standard_seed_rate ||
          ratesDosage.standard_seed_rate == null)
    );

  const dropdownRender = (menu) => (
    <div>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "10px",
          }}
        >
          <Spin size="small" />
        </div>
      ) : (
        menu
      )}
    </div>
  );

  const seedRateUnit =
    ratesDosage.unit_of_rate ||
    trialProtocolData?.product_details?.seeds?.measure_unit;

  return (
    <div className="standard-seedrate-main">
      <div className="seedrate-header">
        <p className="seedrate-heading">
          {formatTranslation(
            t("trials.plotDesign.trialPlots.table.standardSeedRate")
          )}
        </p>

        <Tooltip
          placement="right"
          title={formatTranslation(
            t("trials.plotDesign.standardSeedRate.tooltip")
          )}
        >
          <img src={infoIcon} alt="info-icon" className="seedrate-info-icon" />
        </Tooltip>
      </div>

      {/* Flat flex row — no Form.Item label prop so Ant Design can't push inputs out of alignment */}
      <div className="seedrate-selections">
        {/* Product column */}
        <div className="select-product">
          <label
            className="seedrate-field-label"
            htmlFor="standard-seed-product"
          >
            {formatTranslation(t("trials.plotDesign.trialPlots.table.header2"))}
          </label>

          <ConfigProvider renderEmpty={DropdownEmptyState}>
            <Select
              id="standard-seed-product"
              showSearch
              filterOption={(input, option) =>
                matchesSeedProductSearch(input, productsById.get(option?.value))
              }
              placeholder={formatTranslation(
                t("trials.plotDesign.trialPlots.select")
              )}
              value={selectedProductValue}
              onChange={(product) => {
                handleStandardProductChange(product);
              }}
              className="selected-product"
              popupRender={dropdownRender}
              loading={loading}
            >
              {products.length > 0 &&
                products
                  .slice()
                  .sort((a, b) =>
                    a?.commercial_name?.localeCompare(b?.commercial_name)
                  )
                  .map((product) => (
                    <Option key={product?.id} value={product?.id}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>{product?.commercial_name}</span>

                        <span
                          style={{
                            fontSize: "12px",
                            color: "#999",
                            marginLeft: "8px",
                          }}
                        >
                          {product?.brand ||
                            formatTranslation(
                              t("newTrial.ratesAndDosage.notApplicable")
                            )}{" "}
                          •{" "}
                          {product?.manufacturer ||
                            formatTranslation(
                              t("newTrial.ratesAndDosage.notApplicable")
                            )}
                        </span>
                      </div>
                    </Option>
                  ))}
            </Select>
          </ConfigProvider>

          {shouldDisplayRateError() && (
            <span
              className="error-message"
              style={{ marginTop: "4px", display: "flex" }}
            >
              {formatTranslation(
                t("trials.plotDesign.trialPlots.errorText.rate")
              )}
            </span>
          )}
        </div>

        {/* Seed Rate column */}
        <div className="input-seedrate">
          <label className="seedrate-field-label" htmlFor="standard-seed-rate">
            {formatTranslation(
              t("newTrial.ratesDosage.seedRate.seedRateWithUnit", {
                unit: seedRateUnit,
              })
            )}
          </label>

          <InputNumber
            id="standard-seed-rate"
            min={0}
            precision={2}
            step={0.01}
            style={{ height: "40px" }}
            value={ratesDosage.standard_seed_rate ?? 0}
            onChange={(seedRate) => {
              handleStandardSeedRateChange(seedRate);
            }}
            onKeyDown={restrictToTwoDecimals}
            className="input-number seeds-rate-input-uom"
          />
        </div>
      </div>
    </div>
  );
};

export default StandardSeedRate;
